"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const api_endpoints_1 = require("../api-endpoints");
const application_logger_1 = require("../application-logger");
const attributes_1 = require("../attributes");
const lru_in_memory_assignment_cache_1 = require("../cache/lru-in-memory-assignment-cache");
const non_expiring_in_memory_cache_assignment_1 = require("../cache/non-expiring-in-memory-cache-assignment");
const constants_1 = require("../constants");
const decoding_1 = require("../decoding");
const http_client_1 = require("../http-client");
const interfaces_1 = require("../interfaces");
const obfuscation_1 = require("../obfuscation");
const poller_1 = require("../poller");
const precomputed_requestor_1 = require("../precomputed-requestor");
const validation_1 = require("../validation");
const version_1 = require("../version");
const eppo_client_1 = require("./eppo-client");
class EppoPrecomputedClient {
    constructor(options) {
        this.queuedAssignmentEvents = [];
        this.banditEventsQueue = [];
        this.precomputedFlagStore = options.precomputedFlagStore;
        this.precomputedBanditStore = options.precomputedBanditStore;
        this.overrideStore = options.overrideStore;
        const { subjectKey, subjectAttributes } = options.subject;
        this.subject = {
            subjectKey,
            subjectAttributes: (0, attributes_1.ensureContextualSubjectAttributes)(subjectAttributes),
        };
        this.banditActions = options.banditActions;
        if (options.requestParameters) {
            // Online-mode
            this.requestParameters = options.requestParameters;
        }
        else {
            // Offline-mode -- depends on pre-populated IConfigurationStores (flags and bandits) to source configuration.
            // Allow an empty precomputedFlagStore to be passed in, but if it has items, ensure it was initialized properly.
            if (this.precomputedFlagStore.getKeys().length > 0) {
                if (!this.precomputedFlagStore.isInitialized()) {
                    application_logger_1.logger.error(`${application_logger_1.loggerPrefix} EppoPrecomputedClient requires an initialized precomputedFlagStore if requestParameters are not provided`);
                }
                if (!this.precomputedFlagStore.salt) {
                    application_logger_1.logger.error(`${application_logger_1.loggerPrefix} EppoPrecomputedClient requires a precomputedFlagStore with a salt if requestParameters are not provided`);
                }
            }
            if (this.precomputedBanditStore && !this.precomputedBanditStore.isInitialized()) {
                application_logger_1.logger.error(`${application_logger_1.loggerPrefix} Passing banditOptions without requestParameters requires an initialized precomputedBanditStore`);
            }
            if (this.precomputedBanditStore && !this.precomputedBanditStore.salt) {
                application_logger_1.logger.warn(`${application_logger_1.loggerPrefix} EppoPrecomputedClient missing or empty salt for precomputedBanditStore`);
            }
        }
    }
    async fetchPrecomputedFlags() {
        if (!this.requestParameters) {
            throw new Error('Eppo SDK unable to fetch precomputed flags without the request parameters');
        }
        // if fetchFlagConfigurations() was previously called, stop any polling process from that call
        this.requestPoller?.stop();
        const { apiKey, sdkName, sdkVersion, baseUrl, // Default is set before passing to ApiEndpoints constructor if undefined
        requestTimeoutMs = constants_1.DEFAULT_REQUEST_TIMEOUT_MS, numInitialRequestRetries = constants_1.DEFAULT_INITIAL_CONFIG_REQUEST_RETRIES, numPollRequestRetries = constants_1.DEFAULT_POLL_CONFIG_REQUEST_RETRIES, pollAfterSuccessfulInitialization = false, pollAfterFailedInitialization = false, throwOnFailedInitialization = false, skipInitialPoll = false, } = this.requestParameters;
        const { subjectKey, subjectAttributes } = this.subject;
        let { pollingIntervalMs = constants_1.DEFAULT_POLL_INTERVAL_MS } = this.requestParameters;
        if (pollingIntervalMs <= 0) {
            application_logger_1.logger.error('pollingIntervalMs must be greater than 0. Using default');
            pollingIntervalMs = constants_1.DEFAULT_POLL_INTERVAL_MS;
        }
        // todo: Inject the chain of dependencies below
        const apiEndpoints = new api_endpoints_1.default({
            baseUrl: baseUrl ?? constants_1.PRECOMPUTED_BASE_URL,
            queryParams: { apiKey, sdkName, sdkVersion },
        });
        const httpClient = new http_client_1.default(apiEndpoints, requestTimeoutMs);
        const precomputedRequestor = new precomputed_requestor_1.default(httpClient, this.precomputedFlagStore, subjectKey, subjectAttributes, this.precomputedBanditStore, this.banditActions);
        const pollingCallback = async () => {
            if (await this.precomputedFlagStore.isExpired()) {
                return precomputedRequestor.fetchAndStorePrecomputedFlags();
            }
        };
        this.requestPoller = (0, poller_1.default)(pollingIntervalMs, pollingCallback, {
            maxStartRetries: numInitialRequestRetries,
            maxPollRetries: numPollRequestRetries,
            pollAfterSuccessfulStart: pollAfterSuccessfulInitialization,
            pollAfterFailedStart: pollAfterFailedInitialization,
            errorOnFailedStart: throwOnFailedInitialization,
            skipInitialPoll: skipInitialPoll,
        });
        await this.requestPoller.start();
    }
    stopPolling() {
        if (this.requestPoller) {
            this.requestPoller.stop();
        }
    }
    getPrecomputedAssignment(flagKey, defaultValue, expectedType, valueTransformer = (v) => v) {
        (0, validation_1.validateNotBlank)(flagKey, 'Invalid argument: flagKey cannot be blank');
        const overrideVariation = this.overrideStore?.get(flagKey);
        if (overrideVariation) {
            return valueTransformer(overrideVariation.value);
        }
        const precomputedFlag = this.getPrecomputedFlag(flagKey);
        if (precomputedFlag == null) {
            application_logger_1.logger.warn(`${application_logger_1.loggerPrefix} No assigned variation. Flag not found: ${flagKey}`);
            return defaultValue;
        }
        // Add type checking before proceeding
        if (!(0, eppo_client_1.checkTypeMatch)(expectedType, precomputedFlag.variationType)) {
            const errorMessage = `${application_logger_1.loggerPrefix} Type mismatch: expected ${expectedType} but flag ${flagKey} has type ${precomputedFlag.variationType}`;
            application_logger_1.logger.error(errorMessage);
            return defaultValue;
        }
        const result = {
            flagKey,
            format: this.precomputedFlagStore.getFormat() ?? '',
            subjectKey: this.subject.subjectKey ?? '',
            subjectAttributes: (0, attributes_1.ensureNonContextualSubjectAttributes)(this.subject.subjectAttributes ?? {}),
            variation: {
                key: precomputedFlag.variationKey ?? '',
                value: precomputedFlag.variationValue,
            },
            allocationKey: precomputedFlag.allocationKey ?? '',
            extraLogging: precomputedFlag.extraLogging ?? {},
            doLog: precomputedFlag.doLog,
        };
        try {
            if (result?.doLog) {
                this.logAssignment(result);
            }
        }
        catch (error) {
            application_logger_1.logger.error(`${application_logger_1.loggerPrefix} Error logging assignment event: ${error}`);
        }
        try {
            return result.variation?.value !== undefined
                ? valueTransformer(result.variation.value)
                : defaultValue;
        }
        catch (error) {
            application_logger_1.logger.error(`${application_logger_1.loggerPrefix} Error transforming value: ${error}`);
            return defaultValue;
        }
    }
    /**
     * Maps a subject to a string variation for a given experiment.
     *
     * @param flagKey feature flag identifier
     * @param defaultValue default value to return if the subject is not part of the experiment sample
     * @returns a variation value if a flag was precomputed for the subject, otherwise the default value
     * @public
     */
    getStringAssignment(flagKey, defaultValue) {
        return this.getPrecomputedAssignment(flagKey, defaultValue, interfaces_1.VariationType.STRING);
    }
    /**
     * Maps a subject to a boolean variation for a given experiment.
     *
     * @param flagKey feature flag identifier
     * @param defaultValue default value to return if the subject is not part of the experiment sample
     * @returns a variation value if a flag was precomputed for the subject, otherwise the default value
     * @public
     */
    getBooleanAssignment(flagKey, defaultValue) {
        return this.getPrecomputedAssignment(flagKey, defaultValue, interfaces_1.VariationType.BOOLEAN);
    }
    /**
     * Maps a subject to an integer variation for a given experiment.
     *
     * @param flagKey feature flag identifier
     * @param defaultValue default value to return if the subject is not part of the experiment sample
     * @returns a variation value if a flag was precomputed for the subject, otherwise the default value
     * @public
     */
    getIntegerAssignment(flagKey, defaultValue) {
        return this.getPrecomputedAssignment(flagKey, defaultValue, interfaces_1.VariationType.INTEGER);
    }
    /**
     * Maps a subject to a numeric (floating point) variation for a given experiment.
     *
     * @param flagKey feature flag identifier
     * @param defaultValue default value to return if the subject is not part of the experiment sample
     * @returns a variation value if a flag was precomputed for the subject, otherwise the default value
     * @public
     */
    getNumericAssignment(flagKey, defaultValue) {
        return this.getPrecomputedAssignment(flagKey, defaultValue, interfaces_1.VariationType.NUMERIC);
    }
    /**
     * Maps a subject to a JSON object variation for a given experiment.
     *
     * @param flagKey feature flag identifier
     * @param defaultValue default value to return if the subject is not part of the experiment sample
     * @returns a parsed JSON object if a flag was precomputed for the subject, otherwise the default value
     * @public
     */
    getJSONAssignment(flagKey, defaultValue) {
        return this.getPrecomputedAssignment(flagKey, defaultValue, interfaces_1.VariationType.JSON, (value) => typeof value === 'string' ? JSON.parse(value) : defaultValue);
    }
    getBanditAction(flagKey, defaultValue) {
        const banditEvaluation = this.getPrecomputedBandit(flagKey);
        if (banditEvaluation == null) {
            application_logger_1.logger.warn(`${application_logger_1.loggerPrefix} No assigned variation. Bandit not found: ${flagKey}`);
            return { variation: defaultValue, action: null };
        }
        const assignedVariation = this.getStringAssignment(flagKey, defaultValue);
        const banditEvent = {
            timestamp: new Date().toISOString(),
            featureFlag: flagKey,
            bandit: banditEvaluation.banditKey,
            subject: this.subject.subjectKey ?? '',
            action: banditEvaluation.action,
            actionProbability: banditEvaluation.actionProbability,
            optimalityGap: banditEvaluation.optimalityGap,
            modelVersion: banditEvaluation.modelVersion,
            subjectNumericAttributes: banditEvaluation.actionNumericAttributes,
            subjectCategoricalAttributes: banditEvaluation.actionCategoricalAttributes,
            actionNumericAttributes: banditEvaluation.actionNumericAttributes,
            actionCategoricalAttributes: banditEvaluation.actionCategoricalAttributes,
            metaData: this.buildLoggerMetadata(),
            evaluationDetails: null,
        };
        try {
            this.logBanditAction(banditEvent);
        }
        catch (error) {
            application_logger_1.logger.error(`${application_logger_1.loggerPrefix} Error logging bandit action: ${error}`);
        }
        return { variation: assignedVariation, action: banditEvent.action };
    }
    getPrecomputedFlag(flagKey) {
        return this.getObfuscatedFlag(flagKey);
    }
    getObfuscatedFlag(flagKey) {
        const salt = this.precomputedFlagStore.salt;
        const saltedAndHashedFlagKey = (0, obfuscation_1.getMD5Hash)(flagKey, salt);
        const precomputedFlag = this.precomputedFlagStore.get(saltedAndHashedFlagKey);
        return precomputedFlag ? (0, decoding_1.decodePrecomputedFlag)(precomputedFlag) : null;
    }
    getPrecomputedBandit(banditKey) {
        const obfuscatedBandit = this.getObfuscatedPrecomputedBandit(banditKey);
        return obfuscatedBandit ? (0, decoding_1.decodePrecomputedBandit)(obfuscatedBandit) : null;
    }
    getObfuscatedPrecomputedBandit(banditKey) {
        const salt = this.precomputedBanditStore?.salt;
        const saltedAndHashedBanditKey = (0, obfuscation_1.getMD5Hash)(banditKey, salt);
        const precomputedBandit = this.precomputedBanditStore?.get(saltedAndHashedBanditKey);
        return precomputedBandit ?? null;
    }
    isInitialized() {
        return this.precomputedFlagStore.isInitialized();
    }
    setAssignmentLogger(logger) {
        this.assignmentLogger = logger;
        // log any assignment events that may have been queued while initializing
        this.flushQueuedEvents(this.queuedAssignmentEvents, this.assignmentLogger?.logAssignment);
    }
    setBanditLogger(logger) {
        this.banditLogger = logger;
        // log any bandit events that may have been queued while initializing
        this.flushQueuedEvents(this.banditEventsQueue, this.banditLogger?.logBanditAction);
    }
    /**
     * Assignment cache methods.
     */
    disableAssignmentCache() {
        this.assignmentCache = undefined;
    }
    useNonExpiringInMemoryAssignmentCache() {
        this.assignmentCache = new non_expiring_in_memory_cache_assignment_1.NonExpiringInMemoryAssignmentCache();
    }
    useLRUInMemoryAssignmentCache(maxSize) {
        this.assignmentCache = new lru_in_memory_assignment_cache_1.LRUInMemoryAssignmentCache(maxSize);
    }
    useCustomAssignmentCache(cache) {
        this.assignmentCache = cache;
    }
    flushQueuedEvents(eventQueue, logFunction) {
        const eventsToFlush = [...eventQueue]; // defensive copy
        eventQueue.length = 0; // Truncate the array
        if (!logFunction) {
            return;
        }
        eventsToFlush.forEach((event) => {
            try {
                logFunction(event);
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            }
            catch (error) {
                application_logger_1.logger.error(`${application_logger_1.loggerPrefix} Error flushing event to logger: ${error.message}`);
            }
        });
    }
    logAssignment(result) {
        const { flagKey, subjectKey, allocationKey, subjectAttributes, variation, format } = result;
        const event = {
            ...(result.extraLogging ?? {}),
            allocation: allocationKey ?? null,
            experiment: allocationKey ? `${flagKey}-${allocationKey}` : null,
            featureFlag: flagKey,
            format,
            variation: variation?.key ?? null,
            subject: subjectKey,
            timestamp: new Date().toISOString(),
            subjectAttributes,
            metaData: this.buildLoggerMetadata(),
            evaluationDetails: null,
        };
        if (variation && allocationKey) {
            const hasLoggedAssignment = this.assignmentCache?.has({
                flagKey,
                subjectKey,
                allocationKey,
                variationKey: variation.key,
            });
            if (hasLoggedAssignment) {
                return;
            }
        }
        try {
            if (this.assignmentLogger) {
                this.assignmentLogger.logAssignment(event);
            }
            else if (this.queuedAssignmentEvents.length < constants_1.MAX_EVENT_QUEUE_SIZE) {
                // assignment logger may be null while waiting for initialization, queue up events (up to a max)
                // to be flushed when set
                this.queuedAssignmentEvents.push(event);
            }
            this.assignmentCache?.set({
                flagKey,
                subjectKey,
                allocationKey: allocationKey ?? '__eppo_no_allocation',
                variationKey: variation?.key ?? '__eppo_no_variation',
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }
        catch (error) {
            application_logger_1.logger.error(`${application_logger_1.loggerPrefix} Error logging assignment event: ${error.message}`);
        }
    }
    logBanditAction(banditEvent) {
        // First we check if this bandit action has been logged before
        const subjectKey = banditEvent.subject;
        const flagKey = banditEvent.featureFlag;
        const banditKey = banditEvent.bandit;
        const actionKey = banditEvent.action ?? '__eppo_no_action';
        const banditAssignmentCacheProperties = {
            flagKey,
            subjectKey,
            banditKey,
            actionKey,
        };
        if (this.banditAssignmentCache?.has(banditAssignmentCacheProperties)) {
            // Ignore repeat assignment
            return;
        }
        // If here, we have a logger and a new assignment to be logged
        try {
            if (this.banditLogger) {
                this.banditLogger.logBanditAction(banditEvent);
            }
            else {
                // If no logger defined, queue up the events (up to a max) to flush if a logger is later defined
                this.banditEventsQueue.push(banditEvent);
            }
            // Record in the assignment cache, if active, to deduplicate subsequent repeat assignments
            this.banditAssignmentCache?.set(banditAssignmentCacheProperties);
        }
        catch (err) {
            application_logger_1.logger.warn('Error encountered logging bandit action', err);
        }
    }
    buildLoggerMetadata() {
        return {
            obfuscated: true,
            sdkLanguage: 'javascript',
            sdkLibVersion: version_1.LIB_VERSION,
        };
    }
    setOverrideStore(store) {
        this.overrideStore = store;
    }
    unsetOverrideStore() {
        this.overrideStore = undefined;
    }
}
exports.default = EppoPrecomputedClient;
//# sourceMappingURL=eppo-precomputed-client.js.map